import React, { Component, ReactNode } from 'react';
import { error as logError } from '@/lib/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * GameErrorBoundary
 * 
 * Catches JavaScript errors anywhere in the component tree,
 * logs them, and displays a fallback UI instead of crashing.
 * 
 * Usage:
 * ```tsx
 * <GameErrorBoundary>
 *   <GameBoard />
 * </GameErrorBoundary>
 * ```
 */
export class GameErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to our centralized logger
    logError('React Error Boundary caught an error', error, {
      component: 'GameErrorBoundary',
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // `logError` already dispatches to configured sinks (e.g. Sentry) in production.
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleBackToMenu = () => {
    // Navigate to the current entry point (index.html on web landing,
    // game.html in the native shell) with query/hash stripped, so a
    // crashing deep-link parameter (?battle=…, ?demo=true, …) can't
    // immediately re-trigger the error. A full navigation also resets all
    // React state, which is exactly what we want after a boundary trip.
    // Class component, mounted OUTSIDE ThemeProvider in main.tsx — no
    // hooks/context available here (and none should be: the boundary must
    // render even when a provider is what crashed).
    window.location.href = window.location.pathname;
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI — canonical dark modal chrome (PR #298 family).
      // Static classes (not getModalStyles) on purpose: see handleBackToMenu.
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-zinc-950 to-black flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gradient-to-br from-slate-900 via-zinc-900 to-black border border-violet-500/30 rounded-xl shadow-2xl p-8">
            <div className="flex items-center justify-center w-16 h-16 bg-red-500/15 rounded-full mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-center text-white mb-2">
              Oops! Something went wrong
            </h2>

            <p className="text-zinc-300 text-center mb-6">
              We encountered an unexpected error. Don't worry, your progress might be saved.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-sm font-mono text-red-300 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="text-xs text-red-300/80">
                    <summary className="cursor-pointer font-semibold mb-2">
                      Component Stack
                    </summary>
                    <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 border border-zinc-600 text-zinc-200 font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Reload Page
              </button>
            </div>

            {/* Never dead-end the user: an escape hatch that always leads
                back to the main menu (StackedStreak-45ga). */}
            <button
              onClick={this.handleBackToMenu}
              className="w-full mt-3 bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors shadow-[0_0_18px_rgba(168,85,247,0.35)]"
            >
              Back to Menu
            </button>

            <p className="text-xs text-zinc-500 text-center mt-4">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Lightweight error boundary for modals
 * Shows inline error instead of full-screen
 */
export class ModalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logError('Modal Error Boundary caught an error', error, {
      component: 'ModalErrorBoundary',
      componentStack: errorInfo.componentStack,
    });

    this.setState({ errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleDismiss = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-6 bg-red-500/10 border-2 border-red-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-200 mb-1">
                Something went wrong
              </h3>
              <p className="text-sm text-red-300 mb-3">
                This component encountered an error. Please try again.
              </p>
              {import.meta.env.DEV && this.state.error && (
                <p className="text-xs font-mono text-red-300 mb-3 p-2 bg-black/30 rounded border border-red-500/30">
                  {this.state.error.toString()}
                </p>
              )}
              <button
                onClick={this.handleDismiss}
                className="text-sm bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Compact error boundary for small components
 * Just shows an error message inline
 */
export class CompactErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logError('Compact Error Boundary caught an error', error, {
      component: 'CompactErrorBoundary',
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
          <p className="font-medium">⚠️ Error loading component</p>
          {import.meta.env.DEV && this.state.error && (
            <p className="text-xs mt-1 font-mono">{this.state.error.message}</p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

